"use client";

/**
 * THE toast seam — every product toast is a DeskToast PILL.
 *
 * Operator ruling 2026-08-18 (screenshot round): after the pill redesign,
 * square toasts kept appearing — ~180 call sites across 40 files still
 * called sonner's default renderer (`toast.success/error/info/warning`),
 * which draws the block chrome. This module is a drop-in for those callers:
 * same method names and (title, { description, duration, id }) shape, but
 * every call renders the persona pill via showDeskToast. Import this, never
 * `sonner`, from product code — the only files on raw sonner are the
 * toaster mount and the two pill card modules.
 */

import type * as React from "react";
import { toast as sonnerToast } from "sonner";

import { showDeskToast, type DeskToastTone } from "@/components/notifications/desk-toast";

export interface ToastFacadeOptions {
  description?: React.ReactNode;
  duration?: number;
  id?: string | number;
  action?: { label: string; onClick: () => void };
}

function show(
  tone: DeskToastTone,
  eyebrow: string,
  avatar: boolean,
  title: React.ReactNode,
  options: ToastFacadeOptions = {},
): string | number {
  return showDeskToast({
    id: options.id,
    eyebrow,
    tone,
    avatar,
    title,
    description: options.description,
    action: options.action,
    duration: options.duration,
  });
}

const info = (title: React.ReactNode, options?: ToastFacadeOptions) =>
  show("neutral", "Desk update", true, title, options);

// Callable like sonner's `toast("...")` (bare call = neutral desk update);
// `message` is sonner's neutral alias — both exist in product code.
export const toast = Object.assign(info, {
  success: (title: React.ReactNode, options?: ToastFacadeOptions) =>
    show("mint", "Done", true, title, options),
  error: (title: React.ReactNode, options?: ToastFacadeOptions) =>
    show("rose", "Alert", false, title, options),
  warning: (title: React.ReactNode, options?: ToastFacadeOptions) =>
    show("rose", "Heads up", false, title, options),
  info,
  message: info,
  dismiss: (id?: string | number) => sonnerToast.dismiss(id),
});
